/**
 * 从视频中提取指定时间点的一帧作为 Base64 图片/Blob URL
 * 即使多次尝试后仍是黑屏，也返回该黑屏图片（不报错）
 * 修复 iOS 兼容性问题：事件触发、播放策略、跨域、Promise 状态管理
 */
export function extractVideoFrame(
	videoSrc,
	time = 0.5,
	options = {},
	_retry = 0
) {
	const {
		maxWidth = 300,
			format = 'image/jpeg',
			quality = 0.85
	} = options;

	const MAX_RETRY = 3;
	// iOS 下增加基础延迟，适配事件触发慢的问题
	const IOS_DELAY = 200;

	return new Promise((resolve, reject) => {
		if (!videoSrc) {
			return reject(new Error('视频源为空'));
		}

		const video = document.createElement('video');
		// 1. iOS 跨域处理增强：强制设置 crossorigin（即使同源也不影响）
		video.crossOrigin = 'anonymous';
		video.preload = 'metadata'; // 优先加载元数据，而非完整视频（iOS 推荐）
		video.muted = true;
		video.playsinline = true; // iOS 必须设置，否则会全屏播放
		video.webkitPlaysinline = true;
		video.disablePictureInPicture = true;

		let settled = false;
		let timeoutId = null;
		let onVideoError = null;
		let onVideoAbort = null;

		// 统一的清理函数：确保资源释放且只执行一次
		const cleanup = () => {
			if (timeoutId) clearTimeout(timeoutId);
			// 移除所有事件监听，防止内存泄漏
			video.removeEventListener('loadedmetadata', handleLoadedMetadata);
			video.removeEventListener('loadeddata', handleLoadedData);
			video.removeEventListener('seeked', handleSeeked);
			video.removeEventListener('play', handlePlay);
			if (onVideoError) video.removeEventListener('error', onVideoError);
			if (onVideoAbort) video.removeEventListener('abort', onVideoAbort);

			// 释放视频资源
			if (video.parentNode) video.parentNode.removeChild(video);
			video.pause();
			try {
				video.src = '';
				video.load();
			} catch (e) {
				/* 静默处理 iOS 下的 load 异常 */ }
		};

		// 兜底返回黑屏图片（确保不报错）
		const returnBlackFrame = () => {
			if (settled) return;
			settled = true;
			cleanup();
			// 创建纯黑画布并返回
			const canvas = document.createElement('canvas');
			canvas.width = maxWidth;
			canvas.height = Math.floor(maxWidth * 9 / 16); // 16:9 比例
			const ctx = canvas.getContext('2d');
			ctx.fillStyle = '#000';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			if (canvas.toBlob) {
				canvas.toBlob(blob => {
					resolve(URL.createObjectURL(blob));
				}, format.includes('png') ? 'image/png' : 'image/jpeg', quality);
			} else {
				resolve(canvas.toDataURL(format.includes('png') ? 'image/png' : 'image/jpeg', quality));
			}
		};

		// 错误处理：优先重试，最后兜底返回黑屏
		const handleError = () => {
			if (settled) return;
			// 未达最大重试次数则重试
			if (_retry < MAX_RETRY) {
				settled = true;
				cleanup();
				resolve(extractVideoFrame(videoSrc, time + 0.5, options, _retry + 1));
				return;
			}
			// 最大重试后返回黑屏，不报错
			returnBlackFrame();
		};

		// 2. 先监听元数据加载（iOS 必须等这个事件才能获取 duration）
		const handleLoadedMetadata = () => {
			if (settled) return;
			// 安全设置时间点：避免超出视频时长
			const safeTime = Math.min(time, video.duration - 0.1);
			video.currentTime = Math.max(0.1, safeTime);
			// iOS 下尝试静默播放（解锁 canvas 绘制权限）
			video.play().catch(() => {
				// play 失败不影响，继续走 seeked 逻辑
			});
		};

		// 3. 加载数据完成事件（兜底）
		const handleLoadedData = () => {
			if (settled) return;
			clearTimeout(timeoutId);
		};

		// 4. iOS 下播放事件（确保视频处于可绘制状态）
		const handlePlay = () => {
			if (settled) return;
			video.pause(); // 播放后立即暂停，仅为解锁绘制权限
		};

		// 5. 定位完成事件（核心绘制逻辑）
		const handleSeeked = () => {
			if (settled) return;
			// iOS 下增加延迟，确保帧已加载完成
			setTimeout(() => {
				try {
					const w = video.videoWidth;
					const h = video.videoHeight;
					// 无效尺寸则重试
					if (!w || !h) {
						return handleError('无效视频尺寸');
					}

					const scale = Math.min(1, maxWidth / w);
					const drawW = Math.floor(w * scale);
					const drawH = Math.floor(h * scale);

					const canvas = document.createElement('canvas');
					canvas.width = drawW;
					canvas.height = drawH;
					const ctx = canvas.getContext('2d');

					// iOS 绘制容错：包裹在 try-catch 中
					try {
						ctx.drawImage(video, 0, 0, drawW, drawH);
					} catch (e) {
						return handleError('绘制视频帧失败');
					}

					// 检测黑屏并重试（未达最大重试次数）
					if (_retry < MAX_RETRY) {
						const isBlack = isCanvasBlack(canvas, drawW, drawH);
						if (isBlack) {
							settled = true;
							cleanup();
							return resolve(extractVideoFrame(videoSrc, time + 0.5, options, _retry +
								1));
						}
					}

					// 无论是否黑屏，返回当前帧（核心需求）
					if (settled) return;
					settled = true;
					cleanup();

					if (canvas.toBlob) {
						canvas.toBlob(blob => {
							if (blob) {
								resolve(URL.createObjectURL(blob));
							} else {
								// toBlob 失败则降级为 toDataURL
								resolve(canvas.toDataURL(format.includes('png') ?
									'image/png' : 'image/jpeg', quality));
							}
						}, format.includes('png') ? 'image/png' : 'image/jpeg', quality);
					} else {
						resolve(canvas.toDataURL(format.includes('png') ? 'image/png' :
							'image/jpeg', quality));
					}
				} catch (e) {
					handleError('截图失败: ' + e.message);
				}
			}, IOS_DELAY); // iOS 专用延迟
		};

		// 设置超时（延长至8秒，适配 iOS 加载慢的问题）
		timeoutId = setTimeout(() => {
			handleError('超时（8秒）');
		}, 8000);

		// 绑定事件（按优先级：元数据 -> 播放 -> 加载数据 -> 定位完成 -> 错误）
		video.addEventListener('loadedmetadata', handleLoadedMetadata);
		video.addEventListener('play', handlePlay);
		video.addEventListener('loadeddata', handleLoadedData);
		video.addEventListener('seeked', handleSeeked);
		video.addEventListener('error', () => handleError('视频加载失败'));
		video.addEventListener('abort', () => handleError('加载中断'));

		// 最后设置 src（确保事件都绑定完成）
		video.src = videoSrc;
	});
}

// 黑屏检测（优化 iOS 下的检测逻辑）
function isCanvasBlack(canvas, width, height) {
	try {
		const ctx = canvas.getContext('2d');
		const centerX = Math.floor(width / 2);
		const centerY = Math.floor(height / 2);
		const size = Math.min(10, Math.floor(Math.min(width, height) / 10));

		// 防止越界
		const x = Math.max(0, centerX - size);
		const y = Math.max(0, centerY - size);
		const checkSize = Math.min(size * 2, width - x, height - y);
		if (checkSize <= 0) return true;

		const imageData = ctx.getImageData(x, y, checkSize, checkSize).data;
		let totalBrightness = 0;
		const pixelCount = imageData.length / 4;
		// 统计平均亮度（更鲁棒的黑屏判断）
		for (let i = 0; i < imageData.length; i += 4) {
			totalBrightness += (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
		}
		const avgBrightness = totalBrightness / pixelCount;
		// iOS 下阈值放宽到 15，避免误判
		return avgBrightness <= 15;
	} catch (e) {
		// 检测失败默认认为不是黑屏（避免误重试）
		return false;
	}
}
// 1. 辅助函数：将 Blob URL 或 Base64 转换为 File 对象
export async function blobUrlToFile(blobUrl, filename = 'cover.jpg') {
  // 情况 A: 如果是 Blob URL (blob:http://...)
  if (blobUrl.startsWith('blob:')) {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    
    // 构造 File 对象
    return new File([blob], filename, {
      type: blob.type || 'image/jpeg',
      lastModified: Date.now(),
    });
  } 
  
  // 情况 B: 如果是 Base64 (data:image/jpeg;base64,...)
  if (blobUrl.startsWith('data:')) {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    
    // 尝试从 base64 头解析扩展名，如果失败则用默认
    const typeMatch = blobUrl.match(/data:(image\/\w+);base64/);
    const mimeType = typeMatch ? typeMatch[1] : 'image/jpeg';
    const ext = mimeType.split('/')[1] || 'jpg';
    const finalFilename = filename.includes('.') ? filename : `cover.${ext}`;

    return new File([blob], finalFilename, {
      type: mimeType,
      lastModified: Date.now(),
    });
  }

  throw new Error('未知的图片格式，既不是 blob url 也不是 base64');
}